/**
 * «Записи» → «по сотрудникам» (`/appointments?group=staff`): рейтинг за выбранный отрезок — кто
 * сколько человек записал, сколько из них пришли и забронировали, и каким способом записали
 * (входящий звонок, исходящий по лиду, исходящий по сделке, переписка, ссылка). Человек относится к одному сотруднику — тому, чья запись привела
 * к приходу. Клик по числу — люди ниже; число способа — ссылка: звонки открываются в «Звонках»
 * списком разговоров, после которых записали, переписка и ссылка — в «Чатах» чатами записи. Спека
 * `docs/specs/2026-09-30-appointments-design.md`, «По сотрудникам»; числа — те же, что у
 * `prokat-runner appointments-report --staff`.
 */
import { useState } from "react";
import { Link } from "react-router";

import { type Окно, прошлыеОкна } from "@/components/DateRangePicker";
import { type AppointmentsStaff as Data, methodHref, type StaffCounts, type StaffMethod, type StaffPerson } from "@/lib/appointments";
import { money } from "@/lib/bonus";
import { iso, plusDays } from "@/lib/dates";
import { card, Num, NumLink, pct, Tile } from "@/pages/AppointmentsUi";
import { CHANNEL, WHERE } from "@/pages/BonusZapis";

/**
 * Что показать в списке людей: «пришли» — вместе с забронировавшими, «ещё ждём» — часть не пришедших,
 * «премия» — те, за чью бронь сотрудник получил «Запись.Бронь».
 */
export type StaffStatus = "came" | "booked" | "no_show" | "waiting" | "bonus";
export type StaffPick = { user?: string | undefined; method?: StaffMethod | undefined; status?: StaffStatus | undefined };
export type StaffSort = "recorded" | "came" | "came_pct" | "booked" | "booked_pct" | "bonus";

const STATUS: Record<StaffStatus, string> = {
  came: "пришли",
  booked: "забронировали",
  no_show: "не пришли",
  waiting: "ещё ждём",
  bonus: "премия за бронь",
};
const OUTCOME = { booked: "забронировали", came: "пришли без брони", no_show: "не пришли" } as const;
/** Звонки — три оттенка одного цвета: входящий, исходящий по лиду, исходящий по сделке. */
const METHODS: { id: StaffMethod; label: string; short: string; color: string; hint: string }[] = [
  {
    id: "call_in",
    label: "входящий звонок",
    short: "вх. звонок",
    color: "var(--chart-1)",
    hint: "клиент позвонил сам (или заказал обратный звонок), разговор с сотрудником за 45 минут до записи",
  },
  {
    id: "call_lead",
    label: "исходящий звонок по лиду",
    short: "исх. лид",
    color: "var(--chart-2)",
    hint: "мы позвонили, сделки ещё не было: обзвон лидов, перезвон по пропущенному — сделку завели в разговоре",
  },
  {
    id: "call_deal",
    label: "исходящий звонок по сделке",
    short: "исх. сделка",
    color: "var(--chart-3)",
    hint: "мы позвонили по уже существующей сделке: обзвон сделок «В работе» / «Думают», повторная запись",
  },
  {
    id: "chat",
    label: "переписка",
    short: "переписка",
    color: "var(--ok)",
    hint: "запись из «Чатов» или расширения, либо ответ клиенту в чате за 2 часа до записи",
  },
  // Своего цвета в палитре нет: жёлтый «warn» в тёмной теме сливается с оттенками звонков.
  { id: "link", label: "ссылка", short: "ссылка", color: "oklch(68% 0.13 300)", hint: "клиент сам выбрал время по ссылке на запись" },
  {
    id: "other",
    label: "не определён",
    short: "не опр.",
    color: "var(--fg-faint)",
    hint: "запись в Bitrix: ни звонка, ни переписки рядом в наших данных нет",
  },
];
const METHOD = Object.fromEntries(METHODS.map((m) => [m.id, m])) as Record<StaffMethod, (typeof METHODS)[number]>;

function Dot({ id }: { id: StaffMethod }) {
  return <span className="inline-block size-2 flex-none rounded-full" style={{ background: METHOD[id].color }} />;
}

/**
 * Число записанных способом: звонки — ссылка в «Звонки», переписка и ссылка — в «Чаты»; люди этой
 * ячейки остаются под отбором по способу. «Не определён» открыть негде — как раньше, люди ниже.
 */
function MethodNum({
  d,
  n,
  method,
  user,
  pick,
  setPick,
}: {
  d: Data;
  n: number;
  method: StaffMethod;
  user?: string | undefined;
  pick: StaffPick | null;
  setPick: (p: StaffPick) => void;
}) {
  const to = methodHref(method, { from: d.from, to: d.to, user });
  const p = { user, method };
  if (!to) return <Num n={n} active={same(pick, p)} on={() => setPick(p)} />;
  const chats = method === "chat" || method === "link";
  return <NumLink n={n} to={to} title={chats ? "Открыть чаты, где записали, — в «Чатах»" : "Открыть эти разговоры — в «Звонках»"} />;
}

const ПОКАЗ = 300;
// Колонок вдвое больше, чем у периодов: поля уже, чтобы рейтинг помещался без прокрутки.
const th = "whitespace-nowrap px-1.5 py-1.5 text-right font-medium";
const td = "px-1.5 py-1.5 text-right tabular-nums";
// Шапка рейтинга переносится по словам: шестнадцать колонок в одну строку не помещаются на ноутбуке.
const thWrap = "px-1.5 py-1.5 text-right align-bottom font-medium leading-tight";

/** Окна вида «по сотрудникам»: отрезок не длиннее 92 дней — автора записи читаем из CRM. */
export function staffWindows(): Окно[] {
  const today = new Date();
  return [
    ...прошлыеОкна().filter((o) => !o.label.includes("год")),
    { label: "90 дней", hint: "89 дней назад и сегодня", from: iso(plusDays(today, -89)), to: iso(today) },
  ];
}

const same = (a: StaffPick | null, b: StaffPick) =>
  !!a && a.user === b.user && a.method === b.method && a.status === b.status;

function matches(p: StaffPerson, pick: StaffPick) {
  if (pick.method && p.method !== pick.method) return false;
  // Премия — по тому, кто её получил: обычно записавший, но CRM могла отдать её другому.
  if (pick.status === "bonus") return p.bonus_to.some((b) => !pick.user || b.user === pick.user);
  if (pick.user && p.user !== pick.user) return false;
  if (pick.status === "came") return p.status !== "no_show";
  if (pick.status === "booked") return p.status === "booked";
  if (pick.status === "no_show") return p.status === "no_show";
  if (pick.status === "waiting") return p.reason === "waiting";
  return true;
}

const rate = (c: StaffCounts, sort: StaffSort) =>
  sort === "came_pct"
    ? c.recorded && c.came / c.recorded
    : sort === "booked_pct"
      ? c.came && c.booked / c.came
      : c[sort];

const HINT = {
  came: "пришли на примерку — от записанных",
  booked: "забронировали — от пришедших: воронка по шагам",
  no_show: "не пришли — от записанных",
  waiting: "не пришли, но сделка ещё в «Ждем клиента»: запись впереди или салон не отметил приход",
  bonus: "премия «Запись.Бронь» (120 ₽ за бронь, до 07.10.26 — 60 ₽) по сделкам забронировавших — в строке того, кто её получил",
};

/** Ячейки воронки по шагам: пришли и % от записанных, бронь и % от пришедших, не пришли и %, ещё ждём, премия. */
function Outcome({ c, base, pick, setPick }: { c: StaffCounts; base: StaffPick; pick: StaffPick | null; setPick: (p: StaffPick) => void }) {
  const cell = (n: number, status: StaffStatus) => {
    const p = { ...base, status };
    return <Num n={n} active={same(pick, p)} on={() => setPick(p)} />;
  };
  const bonus = { ...base, status: "bonus" as const };
  return (
    <>
      <td className={td}>{cell(c.came, "came")}</td>
      <td className={`${td} text-fg-muted`}>{pct(c.came, c.recorded)}</td>
      <td className={td}>{cell(c.booked, "booked")}</td>
      <td className={`${td} text-fg-muted`}>{pct(c.booked, c.came)}</td>
      <td className={td}>{cell(c.no_show, "no_show")}</td>
      <td className={`${td} text-fg-muted`}>{pct(c.no_show, c.recorded)}</td>
      <td className={`${td} text-fg-muted`}>{cell(c.waiting, "waiting")}</td>
      <td className={`${td} whitespace-nowrap pr-4`}>
        {c.bonus ? (
          <button
            type="button"
            onClick={() => setPick(bonus)}
            title="Показать, за чьи брони"
            className={`underline decoration-border underline-offset-2 hover:decoration-fg ${
              same(pick, bonus) ? "rounded bg-accent/15 px-1 font-medium" : ""
            }`}
          >
            {money(c.bonus)}
          </button>
        ) : (
          <span className="text-fg-faint">·</span>
        )}
      </td>
    </>
  );
}

function OutcomeHead() {
  return (
    <>
      <th className={th} title={HINT.booked}>
        % от пришедших
      </th>
      <th className={th}>Не пришли</th>
      <th className={th} title={HINT.no_show}>
        %
      </th>
      <th className={th} title={HINT.waiting}>
        ещё ждём
      </th>
      <th className={`${th} pr-4`} title={HINT.bonus}>
        Премия, ₽
      </th>
    </>
  );
}

function Methods({ d, pick, setPick }: { d: Data; pick: StaffPick | null; setPick: (p: StaffPick) => void }) {
  // По убыванию записанных — сразу видно, откуда записей больше всего; «не определён» — внизу.
  const byRecorded = [...d.methods].sort(
    (a, b) => Number(a.code === "other") - Number(b.code === "other") || b.recorded - a.recorded,
  );
  return (
    <section className={card} data-annot="записи по способам">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Каким способом записали</h2>
        <span className="text-[12px] text-fg-faint">
          по убыванию записанных · способ той записи, по которой человек отнесён к сотруднику · «Записали» — звонки или чаты
          записи
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Способ</th>
              <th className={th}>Записали</th>
              <th className={th}>доля</th>
              <th className={th}>Пришли</th>
              <th className={th}>%</th>
              <th className={th}>Бронь</th>
              <OutcomeHead />
            </tr>
          </thead>
          <tbody>
            {byRecorded.map((m) => {
              const base = { method: m.code };
              return (
                <tr key={m.code} className="border-t border-border hover:bg-bg-hover" data-annot={`способ ${m.label}`}>
                  <td className="whitespace-nowrap px-4 py-1.5">
                    <span className="inline-flex cursor-help items-center gap-1.5" title={METHOD[m.code].hint}>
                      <Dot id={m.code} />
                      {m.label}
                    </span>
                  </td>
                  <td className={`${td} font-medium`}>
                    <MethodNum d={d} n={m.recorded} method={m.code} pick={pick} setPick={setPick} />
                  </td>
                  <td className={`${td} text-fg-muted`}>{pct(m.recorded, d.total.recorded)}</td>
                  <Outcome c={m} base={base} pick={pick} setPick={setPick} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Rating({
  d,
  method,
  setMethod,
  sort,
  setSort,
  pick,
  setPick,
}: {
  d: Data;
  method: StaffMethod | undefined;
  setMethod: (m: StaffMethod | undefined) => void;
  sort: StaffSort;
  setSort: (s: StaffSort) => void;
  pick: StaffPick | null;
  setPick: (p: StaffPick) => void;
}) {
  const of = (r: Data["staff"][number]) => (method ? r.methods[method] : r);
  // «Клиент сам» и «не определён» — не сотрудники: всегда внизу, в рейтинг не входят.
  const staff = (r: Data["staff"][number]) => /^\d+$/.test(r.user);
  const rows = d.staff
    .filter((r) => of(r).recorded > 0)
    .sort((a, b) => Number(staff(b)) - Number(staff(a)) || rate(of(b), sort) - rate(of(a), sort) || of(b).recorded - of(a).recorded);
  const total = method ? d.methods.find((m) => m.code === method)! : d.total;
  const head = (label: string, key: StaffSort, title: string, extra = "") => (
    <th className={`${thWrap} ${extra}`}>
      <button
        type="button"
        onClick={() => setSort(key)}
        title={`${title} · упорядочить рейтинг`}
        className={`hover:text-fg ${sort === key ? "text-fg underline underline-offset-2" : ""}`}
      >
        {label}
        {sort === key ? " ↓" : ""}
      </button>
    </th>
  );
  let place = 0;
  return (
    <section className={card} data-annot="рейтинг сотрудников">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Рейтинг сотрудников</h2>
        <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Способ записи" data-annot="отбор по способу">
          {[undefined, ...METHODS.map((m) => m.id)].map((m) => (
            <button
              key={m ?? "all"}
              type="button"
              role="radio"
              aria-checked={method === m}
              onClick={() => setMethod(m)}
              title={m ? `${METHOD[m].label}: ${METHOD[m].hint}` : "записи любым способом"}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[12px] ${
                method === m ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"
              }`}
            >
              {m && <Dot id={m} />}
              {m ? METHOD[m].short : "все способы"}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-fg-faint">
          клик по числу — люди; число способа — звонки или чаты записи; по заголовку — порядок
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="w-6 py-1.5 pl-4 text-right align-bottom font-medium">№</th>
              <th className="px-2 py-1.5 text-left align-bottom font-medium">Сотрудник</th>
              {head("Записали", "recorded", "уникальных людей")}
              {!method &&
                METHODS.map((m) => (
                  <th key={m.id} className={`${thWrap} cursor-help`} title={`${m.label}: ${m.hint}`}>
                    <span className="inline-flex items-center justify-end gap-1.5">
                      <Dot id={m.id} />
                      {m.short}
                    </span>
                  </th>
                ))}
              {head("Пришли", "came", "пришли на примерку")}
              {head("%", "came_pct", HINT.came)}
              {head("Бронь", "booked", "забронировали")}
              {head("% от пришедших", "booked_pct", HINT.booked)}
              <th className={thWrap}>Не пришли</th>
              <th className={thWrap} title={HINT.no_show}>
                %
              </th>
              <th className={thWrap} title={HINT.waiting}>
                ещё ждём
              </th>
              {head("Премия, ₽", "bonus", HINT.bonus, "pr-4")}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const c = of(r);
              const base = { user: r.user, method };
              return (
                <tr key={r.user} className="border-t border-border hover:bg-bg-hover" data-annot={`сотрудник ${r.user}`}>
                  <td className="py-1.5 pl-4 text-right tabular-nums text-fg-faint">{staff(r) ? ++place : ""}</td>
                  <td className={`whitespace-nowrap px-2 py-1.5 ${staff(r) ? "" : "text-fg-muted"}`} title={r.name}>
                    {staff(r) ? r.name : r.name.replace(/ \(.*\)$/, "")}
                  </td>
                  <td className={`${td} font-medium`}>
                    <Num n={c.recorded} active={same(pick, base)} on={() => setPick(base)} />
                  </td>
                  {!method &&
                    METHODS.map((m) => (
                      <td key={m.id} className={td}>
                        <MethodNum d={d} n={r.methods[m.id].recorded} method={m.id} user={r.user} pick={pick} setPick={setPick} />
                      </td>
                    ))}
                  <Outcome c={c} base={base} pick={pick} setPick={setPick} />
                </tr>
              );
            })}
            <tr className="border-t border-border-strong font-medium" data-annot="итог по сотрудникам">
              <td />
              <td className="px-2 py-1.5">Всего</td>
              <td className={td}>
                <Num n={total.recorded} active={same(pick, { method })} on={() => setPick({ method })} />
              </td>
              {!method &&
                METHODS.map((m) => (
                  <td key={m.id} className={td}>
                    <MethodNum d={d} n={d.total.methods[m.id]} method={m.id} pick={pick} setPick={setPick} />
                  </td>
                ))}
              <Outcome c={total} base={{ method }} pick={pick} setPick={setPick} />
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function People({ d, pick }: { d: Data; pick: StaffPick }) {
  const [all, setAll] = useState(false);
  const people = d.people.filter((p) => matches(p, pick));
  const who = pick.user ? (d.staff.find((r) => r.user === pick.user)?.name ?? pick.user) : "все сотрудники";
  const title = [who, pick.method && METHOD[pick.method].label, pick.status ? STATUS[pick.status] : "записали"]
    .filter(Boolean)
    .join(" · ");
  const shown = all ? people : people.slice(0, ПОКАЗ);
  return (
    <section className={card} data-annot="люди записи">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">{title}</h2>
        <span className="text-[12px] text-fg-faint">{people.length} чел.</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Записали</th>
              <th className="px-3 py-1.5 text-left font-medium">Номер</th>
              <th className="px-3 py-1.5 text-left font-medium">Сделки</th>
              <th className="px-3 py-1.5 text-left font-medium">Кто записал</th>
              <th className="px-3 py-1.5 text-left font-medium">Способ</th>
              <th className="px-3 py-1.5 text-left font-medium">Итог</th>
              <th className="px-3 py-1.5 text-left font-medium">Приход</th>
              <th className="px-3 py-1.5 text-left font-medium">Бронь</th>
              <th className="px-3 py-1.5 text-left font-medium">Премия</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={`${p.zapis_deal}-${p.recorded_at}`} className="border-t border-border align-top hover:bg-bg-hover">
                <td
                  className="whitespace-nowrap px-4 py-1.5 tabular-nums"
                  title={`запись, по которой человек отнесён к сотруднику; первая в отрезке — ${p.recorded_at.slice(0, 16)}`}
                >
                  {p.zapis_at.slice(0, 16)}
                </td>
                <td className="px-3 py-1.5 font-mono">
                  {p.phones.length ? (
                    p.phones.map((ph) => (
                      <Link key={ph} to={`/search?q=${ph}`} className="mr-2 hover:underline" title="поиск по номеру">
                        {ph}
                      </Link>
                    ))
                  ) : (
                    <span className="text-fg-faint">нет номера</span>
                  )}
                </td>
                <td className="px-3 py-1.5 font-mono">
                  {p.deals.map((id) => (
                    <Link
                      key={id}
                      to={`/deals/${id}`}
                      className={`mr-2 underline ${id === p.zapis_deal ? "" : "text-fg-muted"}`}
                      title={id === p.zapis_deal ? "карточка сделки — по её записи человек отнесён к сотруднику" : "карточка сделки"}
                    >
                      {id}
                    </Link>
                  ))}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {p.user_name ?? <span className="text-fg-muted">{p.where === "auto" ? "клиент сам" : "не определён"}</span>}
                  <span className="ml-1.5 text-[11.5px] text-fg-faint">{WHERE[p.where]}</span>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  <span className="inline-flex items-center gap-1.5" title={METHOD[p.method].hint}>
                    <Dot id={p.method} />
                    {p.call_id ? (
                      <Link to={`/calls/${p.call_id}`} className="underline decoration-border underline-offset-2 hover:decoration-fg" title={`разговор ${p.call_at?.slice(11, 16) ?? ""} — карточка звонка`}>
                        {METHOD[p.method].label}
                      </Link>
                    ) : p.chat_id ? (
                      <Link to={`/chats/${p.chat_id}`} className="underline decoration-border underline-offset-2 hover:decoration-fg" title="чат, где записали">
                        {METHOD[p.method].label}
                      </Link>
                    ) : (
                      METHOD[p.method].label
                    )}
                    {p.channel && <span className="text-fg-muted">{CHANNEL[p.channel] ?? p.channel}</span>}
                  </span>
                </td>
                <td className="px-3 py-1.5">
                  {p.status === "no_show" ? <span className="text-fg-muted">{p.reason_label}</span> : OUTCOME[p.status]}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{p.visit_at?.slice(0, 16) ?? "·"}</td>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{p.booking_at?.slice(0, 16) ?? "·"}</td>
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                  {p.bonus_to.length
                    ? p.bonus_to.map((b) => (
                        <span key={b.user} className="mr-2" title="проводка «Запись.Бронь» в CRM">
                          {money(b.amount)} ₽
                          {b.user !== p.user && <span className="ml-1 text-fg-muted">{b.name}</span>}
                        </span>
                      ))
                    : "·"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {people.length > shown.length && (
        <button type="button" onClick={() => setAll(true)} className="w-full border-t border-border px-4 py-2 text-left text-[12.5px] text-fg-muted hover:text-fg">
          показать всех — ещё {people.length - shown.length}
        </button>
      )}
    </section>
  );
}

export function StaffView({
  d,
  method,
  setMethod,
  sort,
  setSort,
  pick,
  setPick,
}: {
  d: Data;
  method: StaffMethod | undefined;
  setMethod: (m: StaffMethod | undefined) => void;
  sort: StaffSort;
  setSort: (s: StaffSort) => void;
  pick: StaffPick | null;
  setPick: (p: StaffPick) => void;
}) {
  const t = d.total;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-annot="итог записей">
        <Tile label="Записали, уникальных людей" value={t.recorded} hint={`входов в «ждем клиента» ${d.records}`} />
        <Tile label="Пришли на примерку" value={t.came} hint={`${pct(t.came, t.recorded)} от записанных`} />
        <Tile
          label="Забронировали"
          value={t.booked}
          hint={`${pct(t.booked, t.came)} от пришедших · премия за бронь ${money(t.bonus)} ₽`}
        />
        <Tile
          label="Не пришли"
          value={t.no_show}
          hint={`${pct(t.no_show, t.recorded)} от записанных · из них ещё ждём ${t.waiting}`}
        />
      </div>
      <p className="text-[12px] text-fg-faint">
        Люди за отрезок {d.from} — {d.to}; пришли и бронь — в {d.window_days} дн. от первой записи. Человек отнесён к одному
        сотруднику — тому, чья запись привела к приходу: последняя до брони (нет брони — до прихода), не пришёл —
        последняя в отрезке. Кто записал — журнал правок стадии в CRM, а запись из «Чатов», расширения и по ссылке — свой
        журнал; способ — звонок или переписка сотрудника с клиентом перед записью. Исходящий звонок — «по сделке», если
        сделка уже была к началу разговора, иначе «по лиду». Воронка по шагам: пришли — от записанных, бронь — от
        пришедших. Премия — проводки «Запись.Бронь» в CRM по сделкам забронировавших, в строке получившего.
      </p>
      <Rating d={d} method={method} setMethod={setMethod} sort={sort} setSort={setSort} pick={pick} setPick={setPick} />
      <Methods d={d} pick={pick} setPick={setPick} />
      {pick && <People key={JSON.stringify(pick)} d={d} pick={pick} />}
    </>
  );
}
